import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import {emitChangedWait, obsStatuses, obsTypes} from '../obsUtils';
import renderAcceptSection from './renderAcceptSection';
import AgyleDictionaryField from '../../../../../components/ui/selector/agyleDictionaryField';
import {FormLabel} from '@mui/material';
import {obsStyles} from '../obsStyle';

function ObsRespiratoryDistressWheezing(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [wheezingLevel, setWheezingLevel] = React.useState(allowDefaultValue ? props.wheezing : null);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    const [wheezingLevels, setWheezingLevels] = React.useState([]);

    // For Mobile view
    useEffect(() => {
        if (props.ipodView && props.status !== obsStatuses.new) {
            setWheezingLevel(props.wheezing);
        }
    }, []);

    useEffect(() => {
        let levels = [
            {
                id: 'none',
                description: t('None'),
                score: 0,
                default: true,
            },
            {
                id: 'mild',
                description: t('Mild'),
                score: 1,
            },
            {
                id: 'moderate',
                description: t('Moderate'),
                score: 2,
            },
            {
                id: 'severe',
                description: t('Severe'),
                score: 3,
            },
        ];
        setWheezingLevels(levels);
    }, []);

    function setDefaults() {
        let def = _.find(wheezingLevels, (item) => !!item.default);
        if (def) {
            setWheezingLevel(def);
        }
    }

    useEffect(() => {
        if (!wheezingLevel && allowDefaultValue) {
            if (!props.wheezingLevel) {
                setDefaults();
            } else {
                setWheezingLevel(props.wheezingLevel);
            }
        }
    }, [wheezingLevels, props.wheezing]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        if (!allowDefaultValue) {
            setWheezingLevel(null);
        } else {
            setDefaults();
        }

        clearAccepted();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue = (!_.isNull(wheezingLevel) && status === obsStatuses.new)
            || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) {
            acceptObs();
        }
    }, [wheezingLevel, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    wheezing: wheezingLevel,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [status, wheezingLevel, score, accepted, acceptDate]);

    // const updateScore = () => {
    //     let newScore = 0;
    //     if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
    //         newScore = 0;
    //     } else if (status === obsStatuses.patientCondition) {
    //         newScore = 3;
    //     } else {
    //         if (wheezingLevel) {
    //             newScore = wheezingLevel.score;
    //         }
    //     }
    //     setScore(newScore);
    // };
    // useEffect(() => {
    //     updateScore();
    // }, [status, wheezingLevel]);

    const onLevelChange = (value) => {
        setWheezingLevel(value);
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={12} className={classes.component}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{marginBottom: 15}}
                        >
                            <Grid item xs={12}>
                                <FormLabel className={classes.label}>
                                    {t('Wheezing Level')}
                                </FormLabel>
                            </Grid>

                            <Grid item xs={12}>
                                <AgyleDictionaryField
                                    onChange={(_, value) => onLevelChange(value)}
                                    options={wheezingLevels}
                                    value={wheezingLevel}
                                    getOptionLabel={(option) => option.description}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    extended
                                    noBorder={false}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {renderAcceptSection({
                        classes,
                        t,
                        hideScore: true,
                        score,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: _.isNull(wheezingLevel),
                        showStatusNR: type === obsTypes.pews,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsRespiratoryDistressWheezing.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    wheezing: PropTypes.object,
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};

ObsRespiratoryDistressWheezing.defaultProps = {
    type: obsTypes.news,
    status: obsStatuses.new,
    score: 0,
    wheezing: null,
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsRespiratoryDistressWheezing);